import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { AppDispatch } from '../store/store';
import { setUser, setLoading, setError, logout as logoutAction, setGuest } from '@e-learning/shared/src/store/slices/authSlice';
import { User } from '@e-learning/shared/src/store/types';
import { trackUserLoggedIn, trackUserSignedUp, trackUserLoggedOut } from './analytics/firebaseAnalytics';

// Convert Firebase user to app User type
const mapFirebaseUser = (firebaseUser: FirebaseUser | null): User | null => {
  if (!firebaseUser) return null;
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
  };
};

// Login with email and password
export const loginWithEmail = async (
  email: string,
  password: string,
  dispatch: AppDispatch
): Promise<void> => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(null));
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    // Track login
    if (userCredential.user) {
      trackUserLoggedIn(userCredential.user.uid, 'email');
    }
    // User will be set via onAuthStateChanged listener
  } catch (error: any) {
    dispatch(setError(error.message || 'Login failed'));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Signup with email and password
export const signupWithEmail = async (
  email: string,
  password: string,
  dispatch: AppDispatch
): Promise<void> => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(null));
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    // Track signup
    if (userCredential.user) {
      trackUserSignedUp(userCredential.user.uid, 'email');
    }
    // User will be set via onAuthStateChanged listener
  } catch (error: any) {
    dispatch(setError(error.message || 'Signup failed'));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Logout
export const logout = async (dispatch: AppDispatch): Promise<void> => {
  try {
    dispatch(setLoading(true));
    await signOut(auth);
    trackUserLoggedOut();
    dispatch(logoutAction());
    dispatch(setGuest(true));
  } catch (error: any) {
    dispatch(setError(error.message || 'Logout failed'));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Set guest mode
export const continueAsGuest = (dispatch: AppDispatch): void => {
  dispatch(setGuest(true));
  dispatch(setUser(null));
};

// Initialize auth state listener
export const initAuthListener = (dispatch: AppDispatch): (() => void) => {
  return onAuthStateChanged(auth, async (firebaseUser) => {
    const user = mapFirebaseUser(firebaseUser);
    if (user) {
      // Check admin status from Firestore
      try {
        const { getUserAdminStatus } = await import('./firestore/adminService');
        const isAdmin = await getUserAdminStatus(user.uid);
        user.isAdmin = isAdmin;
      } catch (error) {
        console.warn('Failed to check admin status:', error);
        user.isAdmin = false;
      }
    }
    dispatch(setUser(user));
    if (user) {
      dispatch(setGuest(false));
    }
  });
};

